import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronDown, FiEdit3, FiTrash2, FiCheck, FiX } from 'react-icons/fi';
import Button from '../ui/Button';

/**
 * Accordion-style study entry list grouped by date.
 */
export default function StudyEntryList({
  entries, formatTime,
  onDelete, onEdit, onEditSave, onEditCancel,
  editingEntryId, editHours, editMinutes, editSeconds,
  setEditHours, setEditMinutes, setEditSeconds,
  selectedDate, setSelectedDate,
}) {
  const [expandedDate, setExpandedDate] = useState(null);

  const grouped = entries.reduce((acc, entry) => {
    acc[entry.date] = acc[entry.date] || [];
    acc[entry.date].push(entry);
    return acc;
  }, {});

  const today = new Date().toISOString().split('T')[0];
  const sortedDates = Object.keys(grouped).sort((a, b) => new Date(b) - new Date(a));
  const displayDates = selectedDate ? [selectedDate] : sortedDates;

  return (
    <div className="entry-list-container">
      <div className="entry-list-header">
        <h3 className="entry-list-title">Çalışma Kayıtları</h3>
        <div className="entry-list-filter">
          <input
            type="date"
            className="input"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            style={{ maxWidth: '180px' }}
          />
          {selectedDate && (
            <Button variant="ghost" size="sm" onClick={() => setSelectedDate('')}>
              <FiX size={14} /> Temizle
            </Button>
          )}
        </div>
      </div>

      {displayDates.length === 0 ? (
        <p className="text-muted text-sm" style={{ textAlign: 'center', padding: 'var(--space-6)' }}>
          Henüz çalışma kaydı yok.
        </p>
      ) : (
        displayDates.map((date, i) => {
          const dateEntries = grouped[date] || [];
          const totalDuration = dateEntries.reduce((sum, e) => sum + e.duration, 0);
          const isExpanded = expandedDate === date;
          const isToday = date === today;

          return (
            <motion.div
              key={date}
              className={`entry-date-group ${isToday ? 'entry-date-group--today' : ''}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <button
                className="entry-date-header"
                onClick={() => setExpandedDate(isExpanded ? null : date)}
              >
                <div className="entry-date-info">
                  <span className="entry-date-label">{isToday ? 'Bugün' : date}</span>
                  <span className="entry-date-total">Toplam: {formatTime(totalDuration)}</span>
                </div>
                <motion.span
                  animate={{ rotate: isExpanded ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <FiChevronDown size={16} />
                </motion.span>
              </button>

              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    className="entry-date-content"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    {dateEntries.map((entry) => (
                      <div key={entry.id} className="entry-row">
                        {editingEntryId === entry.id ? (
                          <div className="entry-edit-row">
                            <input type="number" className="input timer-input" min={0} value={editHours} onChange={(e) => setEditHours(Number(e.target.value))} />
                            <span className="text-muted text-xs">sa</span>
                            <input type="number" className="input timer-input" min={0} max={59} value={editMinutes} onChange={(e) => setEditMinutes(Number(e.target.value))} />
                            <span className="text-muted text-xs">dk</span>
                            <input type="number" className="input timer-input" min={0} max={59} value={editSeconds} onChange={(e) => setEditSeconds(Number(e.target.value))} />
                            <span className="text-muted text-xs">sn</span>
                            <Button variant="primary" size="sm" onClick={onEditSave}><FiCheck size={14} /></Button>
                            <Button variant="ghost" size="sm" onClick={onEditCancel}><FiX size={14} /></Button>
                          </div>
                        ) : (
                          <>
                            <span className="entry-time timer-display">{formatTime(entry.duration)}</span>
                            <span className="entry-timestamp text-muted text-xs">
                              {entry.timestamp?.toDate?.()?.toLocaleTimeString?.([], { hour: '2-digit', minute: '2-digit' }) || ''}
                            </span>
                            <div className="entry-actions">
                              <button className="entry-action-btn" onClick={() => onEdit(entry)} aria-label="Düzenle"><FiEdit3 size={14} /></button>
                              <button className="entry-action-btn entry-action-btn--delete" onClick={() => onDelete(entry.id)} aria-label="Sil"><FiTrash2 size={14} /></button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })
      )}
    </div>
  );
}
